import { useEffect, useState } from 'react';
import { Button, Dialog, DialogType, Flex } from 'components';
import { ButtonColors } from 'types';
import { Event } from 'store/api/event';
import { useSelectedCloverDevice } from 'hooks';

import { ReaderChoice } from './reader-choice';

export interface ReaderProblem {
  message: string;
  action?: string | null;
  // The reader that refused (re-review 24 Oct L1: never read later from the
  // store, which may have moved on).
  deviceId?: number;
}

// Owner 24 Oct: the card request never reached a working reader and nothing was
// charged. A pop-up (not a corner alert) says so, keeps the sale, and offers to
// pay on another reader right here when the show has more than one.
// Part J (24 Oct): it never picks a reader for the rep. The rep taps a Ready
// reader (ReaderChoice); the iPad's reader changes only when the rep presses
// Pay (re-review M1: a tap alone never moves the order screen), and the
// request goes out once that change has reached the screen.
export function ReaderProblemDialog({ problem, event, onClose, onRetry, onSelectReader,
  payText = 'Pay with the selected reader', retryText = 'Try again' }: {
  problem: ReaderProblem | null; event?: Event; onClose: () => void; onRetry: () => void;
  onSelectReader?: (deviceId: number) => void; payText?: string; retryText?: string;
}) {
  const { deviceOptions, selectedDeviceId } = useSelectedCloverDevice(event);
  const otherReaders = (deviceOptions?.length ?? 0) > 1;
  const refused = problem?.deviceId ?? selectedDeviceId;
  const [chosen, setChosen] = useState<number | null>(null);
  const [paying, setPaying] = useState<number | null>(null);
  useEffect(() => {
    setChosen(null);
    setPaying(null);
  }, [problem]);
  // Pay: the chosen reader is stored first; the request starts once the
  // screen reads it back.
  useEffect(() => {
    if (paying === null || Number(selectedDeviceId) !== paying) return;
    setPaying(null);
    onRetry();
  }, [paying, selectedDeviceId, onRetry]);
  const pay = () => {
    if (!otherReaders) return onRetry();
    if (chosen === null) return;
    if (Number(selectedDeviceId) === chosen) return onRetry();
    setPaying(chosen);
    onSelectReader?.(chosen);
  };
  const saysNothingCharged = /nothing was charged/i.test(problem?.message ?? '');
  return (
    <Dialog open={!!problem} onClose={onClose} type={DialogType.Complex}
      title="This reader can't take the payment" width="520px"
      closeOnDocumentClick={false} closeOnEscape={false} showCloseButton={false}>
      {() => (
        <Flex column gap={20}>
          <div role="alert" style={{ lineHeight: 1.6 }}>
            <p style={{ margin: 0 }}>{problem?.message}</p>
            <p style={{ margin: '12px 0 0', fontWeight: 700 }}>
              {saysNothingCharged ? 'The items are still in the cart.' : 'Nothing was charged. The items are still in the cart.'}
            </p>
          </div>
          {otherReaders && event ? (
            <ReaderChoice event={event} chosen={chosen} refusedDeviceId={refused}
              onChoose={setChosen} />
          ) : (
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              Turn the reader on and open Cloud Pay Display, then try again, or take another payment method.
            </p>
          )}
          <Button text={otherReaders ? payText : retryText} onClick={pay}
            disabled={(otherReaders && chosen === null) || paying !== null} />
          <Button colorType={ButtonColors.LightBlue} text="Close" onClick={onClose} />
        </Flex>
      )}
    </Dialog>
  );
}

// Owner 24 Oct ("Still waiting" auto-close): Clover shows no charge so far, but an
// offline approval could still upload, so the rep checks before taking the card
// again. A pop-up, not a corner alert; no "nothing was charged", no other reader.
export function CardCheckDialog({ check, onClose }: { check: ReaderProblem | null; onClose: () => void }) {
  return (
    <Dialog open={!!check} onClose={onClose} type={DialogType.Complex}
      title="Check before taking the card again" width="520px"
      closeOnDocumentClick={false} closeOnEscape={false} showCloseButton={false}>
      {() => (
        <Flex column gap={20}>
          <div role="alert" style={{ lineHeight: 1.6 }}>
            <p style={{ margin: 0 }}>{check?.message}</p>
            {check?.action && <p style={{ margin: '12px 0 0', fontWeight: 700 }}>{check.action}</p>}
          </div>
          <Button text="OK" onClick={onClose} />
        </Flex>
      )}
    </Dialog>
  );
}
